/**
 * Credit calculation utilities for file uploads
 * Logic: 1 credit per image, 2 credits per video
 */

import { 
  isImageType, 
  isVideoType, 
  getCreditsForMimeType 
} from './fileTypes';

export interface FileInfo {
  name: string;
  type: string;
  size: number;
}

export interface FileBreakdown {
  images: number;
  videos: number;
  total: number;
}

/**
 * Calculate required credits for an array of files
 * @param files Array of files to calculate credits for
 * @returns Total credits required
 */
export const calculateRequiredCredits = (files: FileInfo[]): number => {
  if (!files || files.length === 0) {
    return 0;
  }

  let totalCredits = 0;

  for (const file of files) {
    const mimeType = file.type.toLowerCase();
    
    // Use centralized credit calculation
    const credits = getCreditsForMimeType(mimeType);
    totalCredits += credits;
  }

  return totalCredits;
};

/**
 * Get breakdown of files by type (images vs videos)
 * @param files Array of files to analyze
 * @returns Breakdown object with counts
 */
export const getFileBreakdown = (files: FileInfo[]): FileBreakdown => {
  if (!files || files.length === 0) {
    return { images: 0, videos: 0, total: 0 };
  }

  let images = 0;
  let videos = 0;

  for (const file of files) {
    const mimeType = file.type.toLowerCase();
    
    // Use centralized type checking (GIF is now blocked)
    if (isImageType(mimeType)) {
      images++;
    } else if (isVideoType(mimeType)) {
      videos++;
    }
  }

  return {
    images,
    videos,
    total: files.length
  };
};

/**
 * Get credit cost for a single file based on its type
 * @param fileType MIME type of the file
 * @returns Credit cost for the file
 */
export const getCreditCostForFile = (fileType: string): number => {
  const mimeType = fileType.toLowerCase();
  
  // Use centralized credit calculation (GIF is now blocked)
  return getCreditsForMimeType(mimeType);
};

/**
 * Get breakdown of credits by file type
 * @param files Array of files to analyze
 * @returns Object with breakdown of images, videos, and total credits
 */
export const getCreditBreakdown = (files: FileInfo[]): {
  images: number;
  videos: number;
  totalCredits: number;
  unsupported: number;
} => {
  if (!files || files.length === 0) {
    return { images: 0, videos: 0, totalCredits: 0, unsupported: 0 };
  }

  let images = 0;
  let videos = 0;
  let unsupported = 0;
  let totalCredits = 0;

  for (const file of files) {
    const mimeType = file.type.toLowerCase();
    
    // Use centralized type checking and credit calculation (GIF is now blocked)
    if (isImageType(mimeType)) {
      images++;
    } else if (isVideoType(mimeType)) {
      videos++;
    } else {
      unsupported++;
    }
    
    // Add credits for this file
    totalCredits += getCreditsForMimeType(mimeType);
  }

  return {
    images,
    videos,
    totalCredits,
    unsupported
  };
};